/**
 * How long carriers actually take, from requests you have already sent.
 *
 * ⚠️ IT SAYS HOW MUCH IT IS STANDING ON, EVERY TIME. The header carries the
 * total number of answered requests behind the whole table, because this is a
 * page somebody would quote in a carrier meeting — and "Travelers: 1.4 days"
 * means something very different drawn from four requests than from four
 * hundred. A number without its sample size is the shape of a fact rather than
 * a fact.
 *
 * ⚠️ AND A CARRIER WITH TOO FEW ANSWERS SHOWS NO TIMING AT ALL. The server
 * withholds it below three; this renders "not enough yet" rather than a blank,
 * so the reason is visible. A blank invites somebody to assume the data is
 * broken and go looking; the honest sentence stops that.
 */
import { useEffect, useState } from 'react';
import { ApiError, api, type CarrierPerformance as Row } from '../lib/api';

const days = (n: number): string => `${n} ${n === 1 ? 'day' : 'days'}`;

export function CarrierPerformance(): JSX.Element | null {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [answered, setAnswered] = useState(0);
  const [scope, setScope] = useState<'alliance' | 'you'>('you');
  const [err, setErr] = useState('');

  useEffect(() => {
    api
      .carrierPerformance()
      .then((r) => {
        setRows(r.carriers);
        setAnswered(r.answered);
        setScope(r.scope);
      })
      .catch((e: unknown) => {
        setRows([]);
        setErr(e instanceof ApiError ? e.message : 'Could not read carrier timings.');
      });
  }, []);

  // Nothing sent yet is not a finding, it is an empty product. Say nothing.
  if (!rows?.length && !err) return null;

  return (
    <section className="panel cp">
      <div className="cp-head">
        <h3>How long carriers take</h3>
        <p>
          Measured from what you have actually sent — the day a request went out to the day the
          answer came back.{' '}
          <b>
            {answered} answered request{answered === 1 ? '' : 's'} behind this
            {scope === 'alliance' ? ', across the alliance' : ''}.
          </b>{' '}
          {answered < 20 && 'Treat it as early days rather than a benchmark.'}
        </p>
      </div>

      {err && <p className="is-warn">{err}</p>}

      <div className="pc-scroll">
        <table className="pc-table">
          <thead>
            <tr>
              <th>Carrier</th>
              <th>Usually</th>
              <th>Range</th>
              <th>Answered</th>
              <th>Still waiting</th>
            </tr>
          </thead>
          <tbody>
            {rows?.map((c) => (
              <tr key={c.carrier} className={c.thin ? 'cp-thin' : ''}>
                <th scope="row">{c.carrier}</th>
                <td>
                  {c.medianDays === null ? (
                    <span className="pc-sub">not enough yet</span>
                  ) : (
                    <>
                      <b style={{ fontWeight: 700 }}>{days(c.medianDays)}</b>
                      {/*
                        ⚠️ THE MEAN ONLY WHEN IT DISAGREES WITH THE MEDIAN. Two
                        numbers that match is noise; a gap between them means the
                        carrier is inconsistent, which is the finding. Printing
                        both always would bury that.
                      */}
                      {c.meanDays !== null && Math.abs(c.meanDays - c.medianDays) >= 2 && (
                        <div className="pc-sub">mean {days(c.meanDays)} — inconsistent</div>
                      )}
                    </>
                  )}
                </td>
                <td>
                  {c.fastestDays === null ? (
                    <span className="pc-sub">—</span>
                  ) : (
                    <span className="pc-sub">
                      {c.fastestDays}–{c.slowestDays} days
                    </span>
                  )}
                </td>
                <td>
                  {c.answered} of {c.sent}
                </td>
                <td>
                  {c.open ? (
                    <>
                      {c.open}
                      {c.oldestOpenDays !== null && (
                        <div className="pc-sub">oldest {days(c.oldestOpenDays)}</div>
                      )}
                    </>
                  ) : (
                    <span className="pc-sub">none</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="cp-note">
        Outstanding requests are counted but never timed — including one as though it had been
        answered today would make a carrier who has ignored you for a month look fast. A carrier
        with fewer than three answers shows no timing, because an average of one is an anecdote.
      </p>
    </section>
  );
}
